<template>
  <div class="device-license-add">
    <div class="page-header">
      <div class="page-title">新增License数量限制</div>
      <div class="page-desc">为指定租户设置可管理的设备License数量上限</div>
    </div>

    <div class="form-card">
      <div class="form-card-title">License限制信息</div>
      <div class="form-card-body">
        <a-form :form="form" layout="vertical">
          <div class="field-grid">
            <a-form-item class="field-full">
              <span slot="label">
                选择租户
                <span class="label-tip">支持搜索租户名称或租户ID</span>
              </span>
              <a-select
                show-search
                allow-clear
                placeholder="请选择需要配置License限制的租户"
                :filter-option="false"
                :not-found-content="tenantLoading ? undefined : null"
                :loading="tenantLoading"
                @search="handleTenantSearch"
                @focus="handleTenantFocus"
                v-decorator="[
                  'tenantId',
                  {
                    rules: [{ required: true, message: '请选择租户' }],
                  },
                ]"
              >
                <a-select-option
                  v-for="item in tenantOptions"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.name }}（{{ item.id }}）
                </a-select-option>
              </a-select>
            </a-form-item>

            <a-form-item label="License配额数量">
              <a-input
                placeholder="请输入License数量"
                suffix="台"
                v-decorator="[
                  'quota',
                  {
                    rules: [
                      { required: true, message: '请输入License配额数量' },
                      {
                        pattern: /^[1-9]\d*$/,
                        message: '请输入正整数',
                      },
                    ],
                  },
                ]"
              />
            </a-form-item>

            <a-form-item label="生效起始日期">
              <a-date-picker
                style="width: 100%"
                placeholder="请选择起始日期"
                v-decorator="[
                  'startDate',
                  {
                    rules: [{ required: true, message: '请选择生效起始日期' }],
                  },
                ]"
              />
            </a-form-item>

            <a-form-item label="生效结束日期">
              <a-date-picker
                style="width: 100%"
                placeholder="请选择结束日期"
                v-decorator="[
                  'endDate',
                  {
                    rules: [{ required: true, message: '请选择生效结束日期' }],
                  },
                ]"
              />
            </a-form-item>

            <a-form-item label="预警阈值">
              <a-input
                placeholder="请输入预警阈值"
                suffix="%"
                v-decorator="[
                  'warningThreshold',
                  {
                    initialValue: '10',
                    rules: [
                      { required: true, message: '请输入预警阈值' },
                      {
                        pattern: /^(100|[1-9]?\d)$/,
                        message: '请输入 0-100 的整数',
                      },
                    ],
                  },
                ]"
              />
            </a-form-item>

            <a-form-item label="备注说明" class="field-full">
              <div class="textarea-count-wrap">
                <a-textarea
                  :rows="4"
                  :maxLength="200"
                  placeholder="请输入备注信息（选填，限 200 字以内）"
                  v-decorator="['remark']"
                  @change="onRemarkChange"
                />
                <span class="textarea-count">{{ remarkLen }}/200</span>
              </div>
            </a-form-item>
          </div>
        </a-form>
      </div>

      <div class="form-actions">
        <a-button @click="handleCancel">取消</a-button>
        <a-button
          type="primary"
          style="margin-left: 8px"
          :loading="saving"
          @click="handleSave"
        >
          保存
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeviceLicenseAdd',
  data() {
    return {
      form: this.$form.createForm(this),
      tenantOptions: [],
      tenantLoading: false,
      saving: false,
      remarkLen: 0,
      _lastTenantKey: undefined,
    }
  },
  created() {
    this.fetchTenantOptions()
  },
  methods: {
    onRemarkChange(e) {
      this.remarkLen = (e.target.value || '').length
    },
    handleTenantSearch(value) {
      const keyword = (value || '').trim()
      if (this._tenantSearchTimer) {
        clearTimeout(this._tenantSearchTimer)
      }
      this._tenantSearchTimer = setTimeout(() => {
        // 与上次检索条件相同则跳过，避免打开弹层时空 search 重复请求导致闪烁
        if (keyword === this._lastTenantKey) return
        this.fetchTenantOptions(keyword)
      }, 300)
    },
    handleTenantFocus() {
      if (!this.tenantOptions.length && !this.tenantLoading) {
        this.fetchTenantOptions()
      }
    },
    // 备注：租户下拉无专用 License 接口，复用平台租户列表；key 支持租户ID精确或租户名模糊
    async fetchTenantOptions(key) {
      this.tenantLoading = true
      try {
        const params = { page: 1, size: 20 }
        const keyword = (key || '').trim()
        this._lastTenantKey = keyword
        if (keyword) params.key = keyword
        const res = await this.$api._getTenantInfoList(params)
        if (res.code == 0) {
          const records = (res.data && res.data.content) || []
          this.tenantOptions = records.map((item) => ({
            id: String(item.id),
            name: item.name,
          }))
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.tenantLoading = false
      }
    },
    handleCancel() {
      this.$router.push({ name: 'DeviceLicense' })
    },
    handleSave() {
      this.form.validateFields(async (err, values) => {
        if (err) return
        const payload = {
          tenantId: values.tenantId,
          quota: Number(values.quota),
          warningThreshold: Number(values.warningThreshold),
          startDate: values.startDate
            ? values.startDate.format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          endDate: values.endDate
            ? values.endDate.format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          remark: values.remark || '',
        }
        this.saving = true
        try {
          const res = await this.$api.addDeviceLicense(payload)
          if (res.code == 0) {
            this.$message.success('保存成功')
            this.$router.push({ name: 'DeviceLicense' })
          } else {
            this.$message.error(res.message || '保存失败')
          }
        } catch (e) {
          console.error(e)
        } finally {
          this.saving = false
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
.device-license-add {
  padding: 16px 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  margin-bottom: 12px;
  flex-shrink: 0;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #151515;
  line-height: 28px;
}

.page-desc {
  margin-top: 4px;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 20px;
}

.form-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px 24px 24px;
  flex: 1;
  height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.form-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #151515;
  margin-bottom: 16px;
  line-height: 24px;
  flex-shrink: 0;
}

.form-card-body {
  flex: 1;
  height: 0;
  overflow-y: auto;
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
}

.field-full {
  grid-column: 1 / -1;
}

.label-tip {
  margin-left: 8px;
  font-size: 12px;
  color: #bfbfbf;
  font-weight: normal;
}

.form-actions {
  flex-shrink: 0;
  text-align: right;
  padding-top: 16px;
  margin-top: 8px;
}

.textarea-count-wrap {
  position: relative;
}

.textarea-count-wrap /deep/ .ant-input {
  padding-bottom: 24px;
}

.textarea-count {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 12px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.45);
  pointer-events: none;
}
</style>
